// 完整版 Exposures.jsx（收單改成 Toggle Switch）
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  Collapse,
  IconButton,
  Typography,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useUserContext } from "context/UserContext";
import { useTranslation } from "react-i18next";

function Exposures() {
  const { t } = useTranslation("common");
  const { posPidArray, idToken } = useUserContext() || {};
  const [data, setData] = useState([]);
  const [expandedPos, setExpandedPos] = useState({});
  const [expandedEvents, setExpandedEvents] = useState({});
  const [thresholdDialog, setThresholdDialog] = useState({
    open: false,
    scope: "match",
    event_id: "",
    pos_pid: "",
    amount: 0,
  });
  const [globalToggle, setGlobalToggle] = useState(false);
  const [thresholdRules, setThresholdRules] = useState([]);
  const [matchOrderStatus, setMatchOrderStatus] = useState({}); // { event_id: boolean }

  // 抓 Threshold Rules API
  const fetchThresholdRules = async () => {
    if (!idToken) return;
    try {
      const res = await axios.get(
        "https://pdapi.astrophant.online/api/qrcode/v1/bet/order-limit/list-threshold/",
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      setThresholdRules(res.data.order_limit || []);
    } catch (err) {
      console.error("Failed to load threshold rules", err);
    }
  };

  // 抓曝光資料與初始化 matchOrderStatus
  const fetchExposureAndOrderStatus = async () => {
    if (!idToken) return;
    try {
      const res = await axios.get(
        "https://pdapi.astrophant.online/api/qrcode/v1/bet/list-exposure/",
        {
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );
      const exposureData = res.data.result || [];
      setData(exposureData);

      const statusMap = {};
      exposureData.forEach((item) => {
        statusMap[item.event_id] = false;
      });

      thresholdRules.forEach((rule) => {
        if (rule.scope === "match" && rule.is_enabled && rule.limit_amount === 0) {
          statusMap[rule.event_id] = true;
        }
      });

      setMatchOrderStatus(statusMap);
    } catch (err) {
      console.error("Failed to fetch exposures", err);
    }
  };

  useEffect(() => {
    if (!idToken) return;

    fetchThresholdRules();
  }, [idToken]);

  useEffect(() => {
    fetchExposureAndOrderStatus();
  }, [thresholdRules, idToken]);

  const normalizePosPidSet = new Set(
    (posPidArray || []).map((obj) => obj.pos_pid?.trim().toLowerCase()).filter(Boolean)
  );

  const grouped = {};
  for (const item of data) {
    const { pos_pid, event_name, outcomes } = item;
    const normalizedPosPid = pos_pid?.trim().toLowerCase();
    if (!normalizePosPidSet.has(normalizedPosPid)) continue;
    const minPnl = Math.min(...Object.values(outcomes).map((o) => o.total_pnl));
    grouped[pos_pid] ??= { totalPnL: 0, events: {} };
    grouped[pos_pid].events[event_name] ??= { totalMinPnL: 0, items: [] };
    grouped[pos_pid].events[event_name].items.push({ ...item, minPnl });
    grouped[pos_pid].events[event_name].totalMinPnL += minPnl;
  }
  for (const pos_pid in grouped) {
    grouped[pos_pid].totalPnL = Object.values(grouped[pos_pid].events).reduce(
      (acc, e) => acc + e.totalMinPnL,
      0
    );
  }

  const handleGlobalToggle = async (checked) => {
    setGlobalToggle(checked);
    if (!idToken) return alert("No idToken available.");
    try {
      await axios.post(
        "https://pdapi.astrophant.online/api/qrcode/v1/bet/order-limit/set-global-toggle/",
        { stop_order: checked },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      alert(checked ? "Global Order Placement Disabled" : "Global Order Placement Enabled");
    } catch (err) {
      alert("Failed to update global toggle.");
      console.error(err);
    }
  };

  const handleMatchToggle = async (matchId, stop) => {
    if (!idToken) return alert("No idToken available.");
    try {
      await axios.post(
        "https://pdapi.astrophant.online/api/qrcode/v1/bet/order-limit/match-toggle/",
        {
          event_ids: matchId, // string，不是 array
          stop_order: stop, // boolean
        },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );
      alert(stop ? `Match ${matchId} order disabled` : `Match ${matchId} order enabled`);
      // 更新本地狀態
      setMatchOrderStatus((prev) => ({ ...prev, [matchId]: stop }));
    } catch (err) {
      alert("Failed to update match toggle.");
      console.error(err);
    }
  };

  const handleThresholdSubmit = async () => {
    if (!idToken) return alert("No idToken available.");
    const { scope, event_id, pos_pid, amount } = thresholdDialog;
    if (!amount || amount <= 0) return alert("Amount should be greater than 0");

    if (scope === "match" && (!event_id || event_id.trim() === "")) {
      return alert("When setting match threshold, event id is required.");
    }

    try {
      await axios.post(
        "https://pdapi.astrophant.online/api/qrcode/v1/bet/order-limit/set-threshold/",
        {
          scope,
          event_id: event_id || "",
          pos_pid: pos_pid || "",
          limit_amount: parseFloat(amount),
          is_enabled: true,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      alert("Threshold set successfully.");
      await fetchThresholdRules();
      setThresholdDialog({ open: false, scope: "match", event_id: "", pos_pid: "", amount: 0 });
    } catch (err) {
      alert("Failed to set threshold.");
      console.error(err);
    }
  };

  const toggleThresholdEnable = async (rule) => {
    if (!idToken) return alert("No idToken available.");
    try {
      await axios.post(
        "https://pdapi.astrophant.online/api/qrcode/v1/bet/order-limit/set-threshold/",
        {
          scope: rule.scope,
          event_id: rule.event_id,
          pos_pid: rule.pos_pid,
          limit_amount: rule.limit_amount,
          is_enabled: !rule.is_enabled,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      await fetchThresholdRules();
    } catch (err) {
      alert("Failed to update threshold rule.");
      console.error(err);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  {t("pageTitles.exposures")}
                </MDTypography>
              </MDBox>
              <MDBox pt={3} pb={3} px={3}>
                {/* 暫時停用全局停單切換 */}
                {/* <FormControlLabel
                  control={
                    <Switch
                      checked={globalToggle}
                      onChange={(e) => handleGlobalToggle(e.target.checked)}
                    />
                  }
                  label={t("exposure.stopOrderGlobally")}
                /> */}

                <Button
                  sx={{ mt: 2, mb: 2, color: "white !important" }}
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    setThresholdDialog({
                      open: true,
                      scope: "match",
                      event_id: "",
                      pos_pid: "",
                      amount: 0,
                    })
                  }
                  disabled={globalToggle}
                >
                  {t("exposure.newThresholdRules")}
                </Button>

                <Grid container spacing={3}>
                  {/* POS 內容 */}
                  <Grid item xs={12} md={6}>
                    {Object.entries(grouped).map(([pos_pid, posData]) => (
                      <Card key={pos_pid} sx={{ mb: 2 }}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          px={3}
                          py={2}
                          sx={{ backgroundColor: "info.main", borderRadius: "lg" }}
                        >
                          <MDTypography variant="h5" fontWeight="bold" color="white">
                            {t("exposure.posPidLabel", { posPid: pos_pid })}
                          </MDTypography>
                          <MDTypography variant="h6" color="white">
                            {t("exposure.worstPnl", { value: posData.totalPnL.toFixed(2) })}
                          </MDTypography>
                          <IconButton
                            onClick={() =>
                              setExpandedPos((prev) => ({ ...prev, [pos_pid]: !prev[pos_pid] }))
                            }
                          >
                            <ExpandMoreIcon sx={{ color: "white" }} />
                          </IconButton>
                        </MDBox>
                        <Collapse in={expandedPos[pos_pid]} timeout="auto" unmountOnExit>
                          <CardContent>
                            {Object.entries(posData.events).map(([event_name, eventData]) => (
                              <Card key={event_name} variant="outlined" sx={{ mb: 2 }}>
                                <MDBox
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  px={2}
                                  py={1}
                                  sx={{ backgroundColor: "secondary.main", borderRadius: "md" }}
                                >
                                  <MDTypography variant="h6" fontWeight="medium" color="white">
                                    {event_name}
                                  </MDTypography>
                                  <MDTypography variant="h6" fontWeight="medium" color="white">
                                    Worst PnL: {eventData.totalMinPnL.toFixed(2)}
                                  </MDTypography>
                                  <IconButton
                                    onClick={() =>
                                      setExpandedEvents((prev) => ({
                                        ...prev,
                                        [pos_pid]: {
                                          ...prev[pos_pid],
                                          [event_name]: !prev[pos_pid]?.[event_name],
                                        },
                                      }))
                                    }
                                    size="small"
                                  >
                                    <ExpandMoreIcon sx={{ color: "white", fontSize: 20 }} />
                                  </IconButton>
                                </MDBox>
                                <Collapse
                                  in={expandedEvents[pos_pid]?.[event_name]}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <CardContent>
                                    {/* Toggle Switch 改收單 */}
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={
                                            matchOrderStatus[eventData.items[0].event_id] || false
                                          }
                                          onChange={(e) =>
                                            handleMatchToggle(
                                              eventData.items[0].event_id,
                                              e.target.checked
                                            )
                                          }
                                          disabled={globalToggle}
                                        />
                                      }
                                      label={
                                        matchOrderStatus[eventData.items[0].event_id]
                                          ? "Place Order Disabled"
                                          : "Place Order Enabled"
                                      }
                                    />
                                    <Button
                                      onClick={() =>
                                        setThresholdDialog({
                                          open: true,
                                          scope: "match",
                                          event_id: eventData.items[0].event_id,
                                          pos_pid: pos_pid,
                                          amount: 0,
                                        })
                                      }
                                      color="info"
                                      disabled={globalToggle}
                                    >
                                      Set threshold
                                    </Button>

                                    {/* 加上 market 與 outcome 明細 */}
                                    {eventData.items.map((item, idx) => (
                                      <MDBox key={idx} mt={2} mb={2} px={1}>
                                        <Typography
                                          variant="subtitle2"
                                          gutterBottom
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          Market: {item.market_name}
                                        </Typography>
                                        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                                          {Object.values(item.outcomes).map((outcome) => (
                                            <li
                                              key={outcome.product_id}
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                padding: "4px 0",
                                                borderBottom: "1px solid #eee",
                                                fontSize: "0.9rem",
                                                color: "#555",
                                              }}
                                            >
                                              <span>{outcome.product_name}</span>
                                              <strong>
                                                Outcome Exposure PnL: {outcome.total_pnl.toFixed(2)}
                                              </strong>
                                            </li>
                                          ))}
                                        </ul>
                                      </MDBox>
                                    ))}
                                  </CardContent>
                                </Collapse>
                              </Card>
                            ))}
                          </CardContent>
                        </Collapse>
                      </Card>
                    ))}
                  </Grid>

                  {/* 門檻列表 */}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {t("exposure.thresholdRules")}
                        </Typography>
                        {thresholdRules.filter((rule) => rule.scope !== "stop").length === 0 ? (
                          <Typography variant="body2">{t("exposure.noThresholdRules")}</Typography>
                        ) : (
                          thresholdRules
                            .filter((rule) => rule.scope !== "stop")
                            .map((rule, idx) => (
                              <MDBox
                                key={idx}
                                sx={{ borderBottom: "1px solid #eee", mb: 1, pb: 1 }}
                              >
                                <Typography variant="body2">
                                  {t("exposure.scope")}: {rule.scope}
                                </Typography>
                                <Typography variant="body2">
                                  {t("exposure.eventId")}: {rule.event_id || "-"}
                                </Typography>
                                <Typography variant="body2">
                                  {t("exposure.posPid")}: {rule.pos_pid || "-"}
                                </Typography>
                                <Typography variant="body2">
                                  {t("exposure.limit")}: ${rule.limit_amount}
                                </Typography>
                                <Typography variant="body2">
                                  {t("exposure.createdAt")}:{" "}
                                  {new Date(rule.create_at).toLocaleString()}
                                </Typography>
                                <Typography variant="body2">
                                  {t("exposure.updatedAt")}:{" "}
                                  {new Date(rule.updated_at).toLocaleString()}
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={rule.is_enabled}
                                      onChange={() => toggleThresholdEnable(rule)}
                                      disabled={globalToggle}
                                    />
                                  }
                                  label={
                                    rule.is_enabled ? t("exposure.enabled") : t("exposure.disabled")
                                  }
                                />
                              </MDBox>
                            ))
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* 新增門檻 Dialog */}
        <Dialog
          open={thresholdDialog.open}
          onClose={() =>
            setThresholdDialog({
              open: false,
              scope: "match",
              event_id: "",
              pos_pid: "",
              amount: 0,
            })
          }
        >
          <DialogTitle>Set Threshold</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              select
              value={thresholdDialog.scope}
              onChange={(e) => setThresholdDialog({ ...thresholdDialog, scope: e.target.value })}
              fullWidth
              sx={{ "& .MuiSelect-select": { minHeight: "48px" } }}
              disabled={globalToggle}
            >
              <MenuItem value="global" sx={{ minHeight: "48px", fontSize: "1rem" }}>
                Global
              </MenuItem>
              <MenuItem value="match" sx={{ minHeight: "48px", fontSize: "1rem" }}>
                Match
              </MenuItem>
            </TextField>
            <TextField
              label="Event ID (No need for Global)"
              value={thresholdDialog.event_id}
              onChange={(e) => setThresholdDialog({ ...thresholdDialog, event_id: e.target.value })}
              fullWidth
              disabled={globalToggle}
            />
            <TextField
              label="POS PID (Optional)"
              value={thresholdDialog.pos_pid}
              onChange={(e) => setThresholdDialog({ ...thresholdDialog, pos_pid: e.target.value })}
              fullWidth
              disabled={globalToggle}
            />
            <TextField
              type="number"
              label="Threshold Amount"
              value={thresholdDialog.amount}
              onChange={(e) =>
                setThresholdDialog({ ...thresholdDialog, amount: parseFloat(e.target.value) })
              }
              fullWidth
              disabled={globalToggle}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setThresholdDialog({
                  open: false,
                  scope: "match",
                  event_id: "",
                  pos_pid: "",
                  amount: 0,
                })
              }
            >
              Cancel
            </Button>
            <Button
              onClick={handleThresholdSubmit}
              sx={{ mt: 2, mb: 2, color: "white !important" }}
              variant="contained"
              disabled={globalToggle}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default Exposures;
